import React, { useState } from "react";
import { Drawer } from "antd";
import { CalibrationIntroStep } from "./CalibrationIntroStep";
import { CalibrationZoomStep } from "./CalibrationZoomStep";
import { CalibrationSeatPlacementStep } from "./CalibrationSeatPlacementStep";
import { CalibrationSeatSizeConfirmStep } from "./CalibrationSeatSizeConfirmStep";
import { CalibrationRowSpacingStep } from "./CalibrationRowSpacingStep";
import { CalibrationDoneStep } from "./CalibrationDoneStep";

const getInitialViewState = () => ({
    zoom: 300,
    offset: { x: 0, y: 0 },
});

const CanvasCalibrationDrawer = ({ visible, onClose, imgSrc, onComplete }) => {
    const [step, setStep] = useState(0);
    const [placedSeats, setPlacedSeats] = useState([]);
    const [seatSize, setSeatSize] = useState(16);
    const [rowSpacing, setRowSpacing] = useState({ x: 0, y: 0 });
    const [viewState, setViewState] = useState(getInitialViewState());

    const handleCloseCalibration = () => {
        setStep(0);
        setPlacedSeats([]);
        setSeatSize(16);
        setRowSpacing({ x: 0, y: 0 });
        setViewState(getInitialViewState());
        onClose();
    };

    const handleFinishCalibration = () => {
        const scale = (viewState?.zoom || 100) / 100;

        const baseRowSeats = [...placedSeats]
            .sort((a, b) => a.x - b.x)
            .slice(0, 4);

        const seatSpacing = getSeatSpacing(baseRowSeats, scale, seatSize);
        const rowSpacingValue = getRowSpacing(rowSpacing.y, scale, seatSize);

        const calibrationResult = {
            imgSrc,
            seatSize,
            spacing: {
                seatSpacing: Math.round(seatSpacing / 2),
                rowSpacing: Math.round(rowSpacingValue),
            },
        };

        handleCloseCalibration();

        if (onComplete) {
            onComplete(calibrationResult);
        }

    };

    const getSeatSpacing = (seats, scale, seatSize) => {
        if (!Array.isArray(seats) || seats.length < 2) return 0;

        const sorted = [...seats].sort((a, b) => a.x - b.x);
        const distances = [];

        for (let i = 1; i < sorted.length; i += 1) {
            const centerToCenter = (sorted[i].x - sorted[i - 1].x) / scale;
            const edgeToEdge = centerToCenter - seatSize;

            if (edgeToEdge >= 0) {
                distances.push(edgeToEdge);
            }
        }

        if (!distances.length) return 0;

        return distances.reduce((sum, value) => sum + value, 0) / distances.length;
    };

    const getRowSpacing = (rowOffsetY, scale, seatSize) => {
        const centerToCenter = rowOffsetY / scale;
        const edgeToEdge = centerToCenter - seatSize;

        return edgeToEdge >= 0 ? edgeToEdge : 0;
    };

    const nextStep = () => setStep((prev) => prev + 1);
    const prevStep = () => setStep((prev) => Math.max(prev - 1, 0));

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <CalibrationIntroStep
                        onNext={nextStep}
                        onClose={handleCloseCalibration}
                    />
                );

            case 1:
                return (
                    <CalibrationZoomStep
                        onNext={nextStep}
                        onBack={prevStep}
                        onClose={handleCloseCalibration}
                        imgSrc={imgSrc}
                        viewState={viewState}
                        setViewState={setViewState}
                    />
                );

            case 2:
                return (
                    <CalibrationSeatPlacementStep
                        onNext={nextStep}
                        onBack={prevStep}
                        onClose={handleCloseCalibration}
                        imgSrc={imgSrc}
                        viewState={viewState}
                        placedSeats={placedSeats}
                        setPlacedSeats={setPlacedSeats}
                        seatSize={seatSize}
                        onSeatSizeChange={setSeatSize}
                    />
                );

            case 3:
                return (
                    <CalibrationSeatSizeConfirmStep
                        onNext={nextStep}
                        onBack={prevStep}
                        onClose={handleCloseCalibration}
                        imgSrc={imgSrc}
                        viewState={viewState}
                        placedSeats={placedSeats}
                        initialSeatSize={seatSize}
                        onSeatSizeChange={setSeatSize}
                    />
                );
            case 4:
                return (
                    <CalibrationRowSpacingStep
                        onNext={nextStep}
                        onBack={prevStep}
                        onClose={handleCloseCalibration}
                        imgSrc={imgSrc}
                        viewState={viewState}
                        placedSeats={placedSeats}
                        seatSize={seatSize}
                        onRowSpacingChange={setRowSpacing}
                    />
                );
            case 5:
                return (
                    <CalibrationDoneStep
                        onDone={handleFinishCalibration}
                        onClose={handleCloseCalibration}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Drawer
            placement="bottom"
            closable={false}
            onClose={onClose}
            visible={visible}
            height="100vh"
            bodyStyle={{
                padding: 0,
                background: "#efefef",
            }}
        >
            {renderStep()}
        </Drawer>
    );
};

export { CanvasCalibrationDrawer };